import React, { useCallback, useState } from 'react';

type OnExpandType = (id: string) => void;
type OnCollapseType = () => void;

interface ActionsGroup {
    id: string,
    title: string,
    items: string[]
}

interface ActionsGroupProps {
    title: string,
    children: React.ReactNode
};

const ActionsGroup = ({ title, children }: ActionsGroupProps) => {
    return (
        <>
            <div className="item item-title">
                <div className="item-inner">{ title }</div>
            </div>

            { children }
        </>
    );
}

interface ActionitemProps {
    id: string,
    isExpanded: boolean,
    onExpand: OnExpandType,
    onCollapse: OnCollapseType,
    renderAction: PopoutListActionsProps['renderAction']
};

const ActionItem = ({ id, isExpanded, onExpand, onCollapse, renderAction }: ActionitemProps) => {
    const handleExpand = useCallback(() => {
        onExpand(id);
    }, [id]);

    return (
        <div className={'item item-action' + (isExpanded ? ' expanded' : '')}>
            { renderAction(id, handleExpand, onCollapse) }
        </div>
    );
}

interface PopoutListActionsProps {
    actions: string[] | ActionsGroup[],
    renderAction: (actionId: string, onExpand: () => void, onCollapse: OnCollapseType) => React.ReactNode,
    updatePopoutPosition?: () => void,
    size?: 'default'
};

const PopoutListActions = ({ actions, renderAction, updatePopoutPosition, size = 'default' }: PopoutListActionsProps) => {
    const [expanded, setExpanded] = useState<string | null>(null);

    const handleExpand = useCallback((id: string) => {
        setExpanded(id);

        setTimeout(() => {
            updatePopoutPosition && updatePopoutPosition();
        }, 100);
    }, [updatePopoutPosition]);

    const handleCollapse = useCallback(() => {
        setExpanded(null);

        setTimeout(() => {
            updatePopoutPosition && updatePopoutPosition();
        }, 100);
    }, [updatePopoutPosition]);

    return (
        <div className={'popout-list popout-list-actions popout-list-' + size + ' ' + (expanded ? ' has-expanded-item' : '')}>
            {
                actions.map(action => typeof action === 'string' ? (
                    <ActionItem
                        key={action}
                        id={action}
                        isExpanded={expanded === action}
                        renderAction={renderAction}
                        onExpand={handleExpand}
                        onCollapse={handleCollapse}
                    />
                ) : (
                    <ActionsGroup title={action.title}>
                        {
                            action.items.map(action => (
                                <ActionItem
                                    key={action}
                                    id={action}
                                    isExpanded={expanded === action}
                                    renderAction={renderAction}
                                    onExpand={handleExpand}
                                    onCollapse={handleCollapse}
                                />
                            ))
                        }
                    </ActionsGroup>
                ))
            }
        </div>
    );
}

export default PopoutListActions;
